import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { apiConfig } from "../config";

import type { Account, Edr, Infringement } from "@/types/domain";
import type { Dashboard } from "@/types/dashboard";
import { MockDataService } from "./mockService";

export interface IDataService {
  getInfringements(params?: {
    lastseen_from?: string;
    lastseen_to?: string;
  }): Promise<{ infringements: Infringement[]; count: number }>;
  getInfringement(id: string): Promise<Infringement>;
  triggerInfringementAction(id: string, action: "block" | "ignore"): Promise<void>;
  getAccount(): Promise<Account>;
  getEdrs(): Promise<Edr[]>;
  getDashboards(): Promise<Dashboard[]>;
  getDashboard(id: string): Promise<Dashboard>;
  createDashboard(config: Dashboard): Promise<Dashboard>;
  updateDashboard(id: string, config: Dashboard): Promise<Dashboard>;
  deleteDashboard(id: string): Promise<void>;
}

class DataService implements IDataService {
  private baseUrl: string;
  private getToken: () => string | undefined;

  constructor(baseUrl: string, getToken: () => string | undefined) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  private async apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    if (!token) throw new Error("No authentication token available");
    const res = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
    });
    if (!res.ok) {
      const body = await res.text();
      throw new Error(`API ${res.status}: ${body}`);
    }
    return res.json() as Promise<T>;
  }

  async getInfringements(
    params?: { lastseen_from?: string; lastseen_to?: string }
  ): Promise<{ infringements: Infringement[]; count: number }> {
    const searchParams = new URLSearchParams();
    if (params?.lastseen_from) searchParams.append("lastseen_from", params.lastseen_from);
    if (params?.lastseen_to) searchParams.append("lastseen_to", params.lastseen_to);
    
    const path = searchParams.toString()
      ? `/api/infringements?${searchParams.toString()}`
      : "/api/infringements";
    return this.apiRequest<{ infringements: Infringement[]; count: number }>(path);
  }

  getInfringement(id: string): Promise<Infringement> {
    return this.apiRequest<Infringement>(`/api/infringements/${encodeURIComponent(id)}`);
  }

  async triggerInfringementAction(id: string, action: "block" | "ignore"): Promise<void> {
    await this.apiRequest<unknown>(`/api/infringements/${encodeURIComponent(id)}/action`, {
      method: "POST",
      body: JSON.stringify({ action }),
    });
  }

  getAccount(): Promise<Account> {
    return this.apiRequest("/api/account");
  }

  async getEdrs(): Promise<Edr[]> {
    const { edrs } = await this.apiRequest<{ edrs: Edr[]; count: number }>("/api/edrs");
    return edrs;
  }

  async getDashboards(): Promise<Dashboard[]> {
    const { dashboards } = await this.apiRequest<{ dashboards: Dashboard[]; count: number }>(
      "/api/dashboards",
    );
    return dashboards;
  }

  getDashboard(id: string): Promise<Dashboard> {
    return this.apiRequest<Dashboard>(`/api/dashboards/${id}`);
  }

  createDashboard(config: Dashboard): Promise<Dashboard> {
    return this.apiRequest<Dashboard>("/api/dashboards", {
      method: "POST",
      body: JSON.stringify({
        name: config.name,
        slug: config.slug,
        description: config.description,
        sources: config.sources,
        status: config.status,
        sections: config.sections,
      }),
    });
  }

  updateDashboard(id: string, config: Dashboard): Promise<Dashboard> {
    return this.apiRequest<Dashboard>(`/api/dashboards/${id}`, {
      method: "PUT",
      body: JSON.stringify({
        name: config.name,
        description: config.description,
        sources: config.sources,
        status: config.status,
        sections: config.sections,
      }),
    });
  }

  async deleteDashboard(id: string): Promise<void> {
    await this.apiRequest<void>(`/api/dashboards/${id}`, { method: "DELETE" });
  }
}

export function useDataService(): IDataService {
  const auth = useAuth();
  return useMemo(() => {
    // MOCK MODE — set VITE_MOCK=true in .env.local to bypass the API and use fake data locally.
    if (import.meta.env.VITE_MOCK === "true") return new MockDataService();
    const base = apiConfig.endpoint ?? (import.meta.env.VITE_API_ENDPOINT as string) ?? "";
    return new DataService(base, () => auth.user?.access_token);
  }, [auth.user?.access_token]);
}
